import Modal from '@components/Modal'
import { useUploadProfilePicture } from '@hooks/useUploadProfilePicture'
import React, { useCallback, useRef, useState } from 'react'
import Cropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'
import toast from 'react-hot-toast'
import { FileInput } from '@components/Field'
import {
    CropActions,
    CropArea,
    CropButton,
    CropHint,
    CropWrapper,
    DropZone,
    DropZoneIcon,
    DropZoneText,
    ZoomSlider,
    ZoomSliderLabel,
    ZoomSliderRow,
} from './EditProfilePictureModal.styles'

interface EditProfilePictureModalProps {
    isOpen: boolean
    onClose: () => void
    onSuccess?: () => void
}

const createCroppedBlob = (imageSrc: string, croppedAreaPixels: Area): Promise<Blob> =>
    new Promise((resolve, reject) => {
        const image = new Image()
        image.src = imageSrc
        image.onload = () => {
            const canvas = document.createElement('canvas')
            const size = Math.min(croppedAreaPixels.width, croppedAreaPixels.height)
            canvas.width = size
            canvas.height = size
            const ctx = canvas.getContext('2d')!
            ctx.drawImage(
                image,
                croppedAreaPixels.x,
                croppedAreaPixels.y,
                croppedAreaPixels.width,
                croppedAreaPixels.height,
                0,
                0,
                size,
                size,
            )
            canvas.toBlob(blob => {
                if (blob) resolve(blob)
                else reject(new Error('Canvas toBlob failed'))
            }, 'image/jpeg', 0.92)
        }
        image.onerror = reject
    })

const EditProfilePictureModal: React.FC<EditProfilePictureModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const uploadProfilePicture = useUploadProfilePicture()

    const [imageSrc, setImageSrc] = useState<string | null>(null)
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)

    const handleFileSelect = (file: File) => {
        const reader = new FileReader()
        reader.onload = () => setImageSrc(reader.result as string)
        reader.readAsDataURL(file)
        setCrop({ x: 0, y: 0 })
        setZoom(1)
    }

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) handleFileSelect(file)
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        const file = e.dataTransfer.files?.[0]
        if (file?.type.startsWith('image/')) handleFileSelect(file)
    }

    const onCropComplete = useCallback((_: Area, pixels: Area) => {
        setCroppedAreaPixels(pixels)
    }, [])

    const handleSave = async () => {
        if (!imageSrc || !croppedAreaPixels) return
        try {
            const blob = await createCroppedBlob(imageSrc, croppedAreaPixels)
            const file = new File([blob], 'profile-picture.jpg', { type: 'image/jpeg' })
            uploadProfilePicture.mutate(file, {
                onSuccess: () => {
                    toast.success('Profile picture updated!')
                    handleClose()
                    onSuccess?.()
                },
                onError: () => {
                    toast.error('Failed to upload picture. Please try again.')
                },
            })
        } catch {
            toast.error('Failed to process image. Please try again.')
        }
    }

    const handleClose = () => {
        setImageSrc(null)
        setCrop({ x: 0, y: 0 })
        setZoom(1)
        setCroppedAreaPixels(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
        onClose()
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Edit Profile Picture"
            showCloseButton
            size="medium"
            footer={imageSrc ? (
                <CropActions>
                    <CropButton
                        type="button"
                        variant="secondary"
                        onClick={() => {
                            setImageSrc(null)
                            if (fileInputRef.current) fileInputRef.current.value = ''
                        }}
                    >
                        Choose Different Photo
                    </CropButton>
                    <CropButton
                        type="button"
                        variant="primary"
                        onClick={handleSave}
                        disabled={uploadProfilePicture.isPending}
                    >
                        {uploadProfilePicture.isPending ? 'Saving…' : 'Save'}
                    </CropButton>
                </CropActions>
            ) : undefined}
        >
            <FileInput
                ref={fileInputRef}
                accept="image/*"
                onChange={handleFileInputChange}
            />

            {!imageSrc ? (
                <DropZone
                    onDrop={handleDrop}
                    onDragOver={e => e.preventDefault()}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <DropZoneIcon>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
                        </svg>
                    </DropZoneIcon>
                    <DropZoneText>
                        <strong>Click to upload</strong> or drag and drop
                        <br />
                        <span>PNG, JPG, WEBP</span>
                    </DropZoneText>
                </DropZone>
            ) : (
                <>
                    <CropWrapper>
                        <CropArea>
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                cropShape="round"
                                showGrid={false}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                            />
                        </CropArea>
                        <CropHint>Drag to reposition · Scroll or pinch to zoom</CropHint>
                        <ZoomSliderRow>
                            <ZoomSliderLabel>Zoom</ZoomSliderLabel>
                            <ZoomSlider
                                min={1}
                                max={3}
                                step={0.01}
                                value={zoom}
                                onChange={e => setZoom(Number(e.target.value))}
                            />
                        </ZoomSliderRow>
                    </CropWrapper>
                </>
            )}
        </Modal>
    )
}

export default EditProfilePictureModal
